/**
 * iOS Safari storage polyfill
 * Fixes localStorage and IndexedDB issues on iOS devices
 */

export function setupIOSStoragePolyfill() {
  // Check if running on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  if (!isIOS) return;
  
  // Test localStorage availability
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
  } catch (e) {
    
    // Create a memory-based fallback
    const memoryStorage: { [key: string]: string } = {};
    
    // Override localStorage methods
    Object.defineProperty(window, 'localStorage', {
      value: {
        getItem: (key: string) => memoryStorage[key] || null,
        setItem: (key: string, value: string) => {
          memoryStorage[key] = value;
        },
        removeItem: (key: string) => {
          delete memoryStorage[key];
        },
        clear: () => {
          Object.keys(memoryStorage).forEach(key => delete memoryStorage[key]);
        },
        key: (index: number) => {
          const keys = Object.keys(memoryStorage);
          return keys[index] || null;
        },
        get length() {
          return Object.keys(memoryStorage).length;
        }
      },
      writable: false,
      configurable: false
    });
  }
  
  // Fix for iOS WKWebView IndexedDB issues
  if ('indexedDB' in window) {
    const originalOpen = indexedDB.open.bind(indexedDB);
    
    indexedDB.open = function(name: string, version?: number) {
      try {
        return originalOpen(name, version);
      } catch (e) {
        // Retry with a delay
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            try {
              resolve(originalOpen(name, version));
            } catch (retryError) {
              reject(retryError);
            }
          }, 100);
        }) as any as IDBOpenDBRequest;
      }
    };
  }
}

// Audio recording permission check for iOS
export async function checkIOSAudioPermission(): Promise<boolean> {
  try {
    // Check if we're in a Capacitor app
    if ((window as any).Capacitor) {
      // For Capacitor apps, permissions should be handled natively
      return true;
    }
    
    // For web, try to get user media permission
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Stop the stream immediately
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    console.error('Audio permission denied:', error);
    return false;
  }
}

// iOS-specific recording fixes
export function setupIOSRecordingFixes() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  if (!isIOS) return;
  
  // Prevent iOS zoom on double-tap
  let lastTouchEnd = 0;
  document.addEventListener('touchend', function(event) {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, false);
  
  // Fix iOS viewport issues
  const viewport = document.querySelector('meta[name=viewport]');
  if (viewport) {
    viewport.setAttribute('content', 
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
  }
}